import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, getToken } from '../lib/api';
import { useAuth, can } from '../lib/auth';
import { RecordPeek, type PeekTarget } from '../components/RecordPeek';

/**
 * Quality — every produced entry waits here until Quality approves it,
 * rejects it or sends it for resorting.  Approve is one tap.  Resorting
 * lists the cartons for the contractor to sort again; they come back here
 * as a Resorting entry for a re-check.  Reject needs the reason; the rejected
 * cartons default to the whole batch (fewer = the rest is accepted), and
 * photos or a PDF can go with the reason.  Built for a phone first.
 *
 * API: /api/quality (VIEW_QC_QUEUE to look, QC_INSPECT to decide,
 * QC_APPROVE to reopen a decision).
 */

interface PendingRow {
  productionLogId: number;
  jobId: string;
  jobName: string | null;
  clientName: string | null;
  poNumber: string | null;
  contractor: string | null;
  operationName: string | null;
  /** BOX for a produced batch; EXTRA for cartons back for a re-check (Resorting). */
  operationKind: string | null;
  machineName: string | null;
  producedQty: number;
  productionDate: string;
  createdOn: string;
  /** The product's material code (A/W Material No) and Design No. */
  materialCode?: string | null;
  designNo?: string | null;
  /** Promised delivery date (yyyy-mm-dd): the job card's, else the PO's. */
  dod?: string | null;
}

interface Attachment { id: number; fileName: string; contentType: string; sizeBytes: number; url: string }

interface HistoryRow {
  qcId: number;
  productionLogId: number;
  jobId: string;
  jobName: string | null;
  clientName: string | null;
  contractor: string | null;
  operationName: string | null;
  productionDate: string;
  inspectionDate: string;
  producedQty: number;
  acceptedQty: number;
  rejectedQty: number;
  reworkQty: number;
  status: 'QC_APPROVED' | 'QC_REJECTED' | 'QC_REWORK';
  rejectionReason: string | null;
  remarks: string | null;
  inspectorName: string | null;
  createdOn: string;
  attachments: Attachment[];
  /** For-resorting list: Resorting entries on the job since, and how much of that Quality passed. */
  resortedQty?: number;
  resortedPassed?: number;
}

const MAX_FILES = 10;

function friendlyQcError(e: unknown): string {
  const msg = e instanceof Error ? e.message : String(e);
  if (msg === 'rejection_requires_reason') return 'Write why it was rejected.';
  if (msg.startsWith('batch_already_inspected')) return 'Someone has already decided this entry — the list is refreshed.';
  if (msg.startsWith('qty_sum_mismatch')) return 'The cartons must be between 1 and the batch quantity.';
  if (msg === 'unsupported_file_type') return 'Only photos (JPG, PNG, WEBP) and PDF files can be attached.';
  if (msg.startsWith('file_too_large')) return `A file is too big (over ${msg.split(':')[1] ?? '25'} MB).`;
  if (msg.startsWith('too_many_files')) return `Attach at most ${MAX_FILES} files.`;
  if (msg === 'request_failed_413') return 'The files are too big together — attach fewer or smaller ones.';
  if (msg === 'qc_attachments_migration_pending') return 'Attachments need the server database update first — reject without files for now.';
  if (msg === 'qc_migration_pending') return 'The server database needs its update (Quality) first.';
  if (msg === 'batch_not_found') return 'This entry no longer exists — the list is refreshed.';
  if (msg === 'forbidden') return 'You do not have permission for this.';
  return msg;
}

const dayText = (iso: string | null | undefined) =>
  (iso ? new Date(`${iso.slice(0, 10)}T00:00:00Z`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', timeZone: 'UTC' }) : '');

const jobNo = (jobId: string) => jobId.split('/').pop() ?? jobId;

/**
 * A phone photo is 3–8 MB; the reason needs far less.  Resize to 1600 px on
 * the long side as JPEG before upload, so a few photos go up quickly on mobile
 * data and stay under the server's limit.  Anything that cannot be decoded
 * here (a PDF, an odd format) goes up as it is and the server decides.
 */
async function shrinkPhoto(file: File): Promise<File> {
  if (!file.type.startsWith('image/')) return file;
  try {
    const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const scale = Math.min(1, 1600 / Math.max(bmp.width, bmp.height));
    if (scale === 1 && file.size <= 1_500_000 && /^image\/(jpeg|png|webp)$/.test(file.type)) return file;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bmp.width * scale);
    canvas.height = Math.round(bmp.height * scale);
    canvas.getContext('2d')?.drawImage(bmp, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.82));
    if (!blob) return file;
    return new File([blob], `${file.name.replace(/\.[^.]+$/, '') || 'photo'}.jpg`, { type: 'image/jpeg' });
  } catch {
    return file;
  }
}

/**
 * Open an attachment.  The file needs the login token, which a plain link
 * cannot send — so fetch it, then show it.  The tab opens on the tap itself
 * so the browser does not block it as a pop-up.
 */
async function openAttachment(a: Attachment) {
  const w = window.open('', '_blank');
  try {
    const res = await fetch(a.url, { headers: { authorization: `Bearer ${getToken() ?? ''}` } });
    if (!res.ok) throw new Error(String(res.status));
    const url = URL.createObjectURL(await res.blob());
    if (w) w.location.href = url; else window.location.assign(url);
    setTimeout(() => URL.revokeObjectURL(url), 120_000);
  } catch {
    w?.close();
    alert('Could not open the file.');
  }
}

/** The Quality page itself. */
export function QualityDashboardPage() {
  return (
    <div className="p-4 md:p-6 space-y-4 max-w-6xl">
      <QualityWork title="Quality" />
    </div>
  );
}

function QualityWork({ title }: { title?: string }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canInspect = can(user, 'QC_INSPECT');
  const canReopen = can(user, 'QC_APPROVE');
  const [tab, setTab] = useState<'pending' | 'resorting' | 'history'>('pending');
  const [search, setSearch] = useState('');
  /** The job card open for a look. */
  const [peek, setPeek] = useState<PeekTarget | null>(null);
  const [notice, setNotice] = useState<{ ok: boolean; text: string; undoQcId?: number } | null>(null);

  const pending = useQuery({
    queryKey: ['qc-pending'],
    queryFn: () => api<{ rows: PendingRow[] }>('/api/quality/pending'),
    refetchInterval: 60_000,
  });
  const history = useQuery({
    enabled: tab === 'history',
    queryKey: ['qc-history'],
    queryFn: () => api<{ rows: HistoryRow[] }>('/api/quality/history?limit=200'),
  });

  // Always loaded: the tab shows how many are out for resorting.
  const resorting = useQuery({
    queryKey: ['qc-resorting'],
    queryFn: () => api<{ rows: HistoryRow[] }>('/api/quality/resorting'),
    refetchInterval: 120_000,
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['qc-resorting'] });
    void qc.invalidateQueries({ queryKey: ['qc-pending'] });
    void qc.invalidateQueries({ queryKey: ['qc-history'] });
    void qc.invalidateQueries({ queryKey: ['production'] });
    void qc.invalidateQueries({ queryKey: ['flow-queue'] });
  };

  const reopen = useMutation({
    mutationFn: (qcId: number) => api(`/api/quality/inspections/${qcId}/reopen`, { method: 'POST' }),
    onSuccess: () => { setNotice({ ok: true, text: 'Back in the list to check again.' }); refresh(); },
    onError: (e) => setNotice({ ok: false, text: friendlyQcError(e) }),
  });

  const s = search.trim().toLowerCase();
  const match = (r: { jobId: string; jobName: string | null; clientName: string | null; contractor: string | null }) =>
    !s || [r.jobId, r.jobName, r.clientName, r.contractor].some((v) => (v ?? '').toLowerCase().includes(s));
  const rows = (pending.data?.rows ?? []).filter(match);
  const histRows = (history.data?.rows ?? []).filter(match);
  const resortRows = (resorting.data?.rows ?? []).filter(match);
  /** Still out: more sent for resorting than has come back and been passed. */
  const stillOut = (r: HistoryRow) => r.reworkQty - (r.resortedPassed ?? 0) > 0;
  const resortingCount = (resorting.data?.rows ?? []).filter(stillOut).length;
  const pendingCount = pending.data?.rows.length ?? 0;

  return (
    <div className="space-y-4">
      {peek && <RecordPeek target={peek} onClose={() => setPeek(null)} />}
      {title && (
        <div>
          <h1 className="text-xl font-semibold text-brand">{title}</h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Approve or reject each production entry, or send it for resorting. A rejection needs the reason — attach photos or a PDF if you have them.
          </p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded border border-slate-200 bg-white text-sm">
          <button
            className={`px-3 py-1.5 rounded-l ${tab === 'pending' ? 'bg-brand text-white' : 'text-slate-600 hover:bg-slate-50'}`}
            onClick={() => setTab('pending')}
          >To check ({pendingCount})</button>
          <button
            className={`px-3 py-1.5 border-l border-slate-200 ${tab === 'resorting' ? 'bg-amber-500 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
            onClick={() => setTab('resorting')}
          >For resorting ({resortingCount})</button>
          <button
            className={`px-3 py-1.5 rounded-r border-l border-slate-200 ${tab === 'history' ? 'bg-brand text-white' : 'text-slate-600 hover:bg-slate-50'}`}
            onClick={() => setTab('history')}
          >Done</button>
        </div>
        <input
          className="input max-w-xs"
          placeholder="Search job, product, party, contractor"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {notice && (
        <div className={`flex flex-wrap items-center gap-3 rounded border px-3 py-2 text-sm ${notice.ok ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-red-200 bg-red-50 text-red-700'}`}>
          <span>{notice.text}</span>
          {notice.undoQcId != null && canReopen && (
            <button
              className="font-semibold underline"
              disabled={reopen.isPending}
              onClick={() => reopen.mutate(notice.undoQcId!)}
            >Undo</button>
          )}
          <button className="ml-auto text-xs opacity-70 hover:opacity-100" onClick={() => setNotice(null)}>✕</button>
        </div>
      )}

      {tab === 'pending' && (
        <>
          {pending.isLoading && <p className="text-sm text-slate-500">Loading…</p>}
          {pending.isError && <p className="text-sm text-red-600">{friendlyQcError(pending.error)}</p>}
          {pending.data && rows.length === 0 && (
            <p className="rounded border border-slate-200 bg-white px-4 py-8 text-center text-sm text-slate-400">
              {pendingCount ? 'Nothing matches the search.' : 'Nothing to check — every production entry has been decided.'}
            </p>
          )}
          <div className="grid gap-3 md:grid-cols-2">
            {rows.map((r) => (
              <PendingCard
                key={r.productionLogId}
                row={r}
                canInspect={canInspect}
                onPeek={(jobId) => setPeek({ kind: 'job', id: jobId })}
                onDone={(text, undoQcId) => { setNotice({ ok: true, text, undoQcId }); refresh(); }}
                onError={(e) => {
                  setNotice({ ok: false, text: friendlyQcError(e) });
                  const m = e instanceof Error ? e.message : '';
                  if (m.startsWith('batch_already_inspected') || m === 'batch_not_found') refresh();
                }}
              />
            ))}
          </div>
        </>
      )}

      {tab === 'resorting' && (
        <>
          {resorting.isLoading && <p className="text-sm text-slate-500">Loading…</p>}
          {resorting.isError && <p className="text-sm text-red-600">{friendlyQcError(resorting.error)}</p>}
          {resorting.data && resortRows.length === 0 && (
            <p className="rounded border border-slate-200 bg-white px-4 py-8 text-center text-sm text-slate-400">Nothing has been sent for resorting.</p>
          )}
          {resortRows.length > 0 && (
            <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-[11px] uppercase text-slate-500">
                  <tr>
                    <th className="px-3 py-2">Job</th>
                    <th className="px-3 py-2">Product</th>
                    <th className="px-3 py-2">Contractor · Box</th>
                    <th className="px-3 py-2 text-right">For resorting</th>
                    <th className="px-3 py-2 text-right">Resorted since</th>
                    <th className="px-3 py-2 text-right">Passed</th>
                    <th className="px-3 py-2">Why</th>
                    <th className="px-3 py-2">Sent</th>
                    {canReopen && <th className="px-3 py-2" />}
                  </tr>
                </thead>
                <tbody>
                  {resortRows.map((r) => {
                    const out = stillOut(r);
                    return (
                      <tr key={r.qcId} className={`border-t border-slate-100 align-top ${out ? '' : 'text-slate-400'}`}>
                        <td className="px-3 py-2 font-mono font-semibold whitespace-nowrap">{jobNo(r.jobId)}</td>
                        <td className="px-3 py-2">
                          <div>{r.jobName ?? '—'}</div>
                          <div className="text-xs text-slate-400">{r.clientName}</div>
                        </td>
                        <td className="px-3 py-2 text-xs">{[r.contractor, r.operationName].filter(Boolean).join(' · ')}</td>
                        <td className="px-3 py-2 text-right font-bold tabular-nums text-amber-700 whitespace-nowrap">
                          {r.reworkQty.toLocaleString()}
                          <div className="text-[11px] font-normal text-slate-400">of {r.producedQty.toLocaleString()}</div>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">{(r.resortedQty ?? 0).toLocaleString()}</td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {(r.resortedPassed ?? 0).toLocaleString()}
                          {!out && <div className="text-[11px] font-semibold text-emerald-600">done</div>}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {r.rejectionReason ?? '—'}
                          {r.attachments.length > 0 && (
                            <div className="mt-1 flex flex-wrap gap-1">
                              {r.attachments.map((a) => (
                                <button key={a.id} className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[11px] text-brand hover:bg-slate-100"
                                  onClick={() => void openAttachment(a)} title={a.fileName}>{a.contentType === 'application/pdf' ? '📄' : '🖼'}</button>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs whitespace-nowrap">{dayText(r.inspectionDate)}{r.inspectorName ? ` · ${r.inspectorName}` : ''}</td>
                        {canReopen && (
                          <td className="px-3 py-2 text-right">
                            <button className="text-xs text-slate-500 hover:text-slate-800 hover:underline" disabled={reopen.isPending}
                              onClick={() => { if (confirm(`Take Job ${jobNo(r.jobId)} off the resorting list and put it back to check again?`)) reopen.mutate(r.qcId); }}>
                              Reopen
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-xs text-slate-500">
            When the contractor has sorted the cartons again, enter them on Production Entry as <b>Resorting</b> for the same job —
            they come back to “To check” as a re-check. “Resorted since” and “Passed” count those entries.
          </p>
        </>
      )}

      {tab === 'history' && (
        <>
          {history.isLoading && <p className="text-sm text-slate-500">Loading…</p>}
          {history.isError && <p className="text-sm text-red-600">{friendlyQcError(history.error)}</p>}
          {history.data && histRows.length === 0 && (
            <p className="rounded border border-slate-200 bg-white px-4 py-8 text-center text-sm text-slate-400">No decisions yet.</p>
          )}
          <div className="space-y-2">
            {histRows.map((r) => (
              <HistoryCard
                key={r.qcId}
                row={r}
                canReopen={canReopen}
                reopening={reopen.isPending}
                onReopen={() => {
                  if (confirm(`Put Job ${jobNo(r.jobId)} (${r.producedQty.toLocaleString()} cartons) back in the list to check again?`)) {
                    reopen.mutate(r.qcId);
                  }
                }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function PendingCard({ row, canInspect, onPeek, onDone, onError }: {
  row: PendingRow;
  canInspect: boolean;
  /** Open the job card. */
  onPeek: (jobId: string) => void;
  onDone: (text: string, undoQcId?: number) => void;
  onError: (e: unknown) => void;
}) {
  const [rejecting, setRejecting] = useState(false);
  /** The "send for resorting" panel. */
  const [resorting, setResorting] = useState(false);
  const [resortQty, setResortQty] = useState(String(row.producedQty));
  const [reason, setReason] = useState('');
  const [rejectedQty, setRejectedQty] = useState(String(row.producedQty));
  const [files, setFiles] = useState<File[]>([]);
  const [preparing, setPreparing] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const approve = useMutation({
    mutationFn: () => api<{ qcId: number }>('/api/quality/inspect', {
      method: 'POST',
      body: JSON.stringify({ productionLogId: row.productionLogId, acceptedQty: row.producedQty, rejectedQty: 0, reworkQty: 0 }),
    }),
    onSuccess: (r) => onDone(`Approved — Job ${jobNo(row.jobId)}, ${row.producedQty.toLocaleString()} cartons.`, r.qcId),
    onError,
  });

  const rej = Number(rejectedQty);
  const rejValid = Number.isInteger(rej) && rej >= 1 && rej <= row.producedQty;
  const canReject = rejValid && reason.trim().length >= 3 && !preparing;

  const reject = useMutation({
    mutationFn: async () => {
      const form = new FormData();
      form.set('productionLogId', String(row.productionLogId));
      form.set('rejectedQty', String(rej));
      form.set('acceptedQty', String(row.producedQty - rej));
      form.set('reworkQty', '0');
      form.set('rejectionReason', reason.trim());
      for (const f of files) form.append('files', f, f.name);
      return api<{ qcId: number; attachments: number }>('/api/quality/inspect', { method: 'POST', body: form });
    },
    onSuccess: (r) => onDone(
      `Rejected — Job ${jobNo(row.jobId)}, ${rej.toLocaleString()} of ${row.producedQty.toLocaleString()} cartons`
        + `${r.attachments ? `, ${r.attachments} file${r.attachments === 1 ? '' : 's'} attached` : ''}.`,
      r.qcId,
    ),
    onError,
  });

  async function addFiles(list: FileList | null) {
    if (!list?.length) return;
    setPreparing(true);
    try {
      const shrunk = await Promise.all([...list].map(shrinkPhoto));
      setFiles((f) => [...f, ...shrunk].slice(0, MAX_FILES));
    } finally {
      setPreparing(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  const rs = Number(resortQty);
  const rsValid = Number.isInteger(rs) && rs >= 1 && rs <= row.producedQty;
  const resort = useMutation({
    mutationFn: async () => {
      const form = new FormData();
      form.set('productionLogId', String(row.productionLogId));
      form.set('rejectedQty', '0');
      form.set('reworkQty', String(rs));
      form.set('acceptedQty', String(row.producedQty - rs));
      if (reason.trim()) form.set('rejectionReason', reason.trim());
      for (const f of files) form.append('files', f, f.name);
      return api<{ qcId: number; attachments: number }>('/api/quality/inspect', { method: 'POST', body: form });
    },
    onSuccess: (r) => onDone(
      `For resorting — Job ${jobNo(row.jobId)}, ${rs.toLocaleString()} of ${row.producedQty.toLocaleString()} cartons`
        + `${rs < row.producedQty ? `; ${(row.producedQty - rs).toLocaleString()} approved` : ''}.`,
      r.qcId,
    ),
    onError,
  });

  const busy = approve.isPending || reject.isPending || resort.isPending;

  return (
    <div className={`rounded-lg border bg-white p-3 shadow-sm ${rejecting ? 'border-red-300' : resorting ? 'border-amber-300' : 'border-slate-200'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-800">
            Job{' '}
            <button type="button" className="font-mono text-brand hover:underline" title="View the job card"
                    onClick={() => onPeek(row.jobId)}>
              {jobNo(row.jobId)}
            </button>
            <span className="ml-2 text-xs font-normal text-slate-500">{dayText(row.productionDate)}</span>
            {row.operationKind === 'EXTRA' && (
              <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold text-amber-800"
                    title="Cartons sorted again after a rejection">Re-check · {row.operationName}</span>
            )}
          </div>
          <div className="truncate text-sm text-slate-700" title={row.jobName ?? ''}>{row.jobName ?? '—'}</div>
          <div className="text-xs text-slate-500">
            {[row.clientName, row.operationName, row.contractor, row.machineName].filter(Boolean).join(' · ')}
          </div>
          <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs">
            <span><span className="text-slate-400">Material code:</span> <span className="font-medium text-slate-700">{row.materialCode || '—'}</span></span>
            <span><span className="text-slate-400">Design no:</span> <span className="font-medium text-slate-700">{row.designNo || '—'}</span></span>
            <span><span className="text-slate-400">DOD:</span> <span className="font-medium text-slate-700">{row.dod ? dayText(row.dod) : '—'}</span></span>
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className="text-lg font-bold tabular-nums text-slate-800">{row.producedQty.toLocaleString()}</div>
          <div className="text-[11px] text-slate-500">cartons</div>
        </div>
      </div>

      {canInspect && !rejecting && !resorting && (
        <div className="mt-3 grid grid-cols-3 gap-2">
          <button
            className="btn bg-emerald-600 text-white hover:bg-emerald-700"
            disabled={busy}
            onClick={() => approve.mutate()}
          >{approve.isPending ? 'Approving…' : '✓ Approve'}</button>
          <button
            className="btn bg-red-600 text-white hover:bg-red-700"
            disabled={busy}
            onClick={() => setRejecting(true)}
          >✗ Reject</button>
          <button
            className="btn bg-amber-500 text-white hover:bg-amber-600"
            disabled={busy}
            title="List these cartons for the contractor to sort again"
            onClick={() => setResorting(true)}
          >↻ Resorting</button>
        </div>
      )}

      {canInspect && resorting && (
        <div className="mt-3 space-y-2 rounded border border-amber-200 bg-amber-50 p-2.5">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="label">Cartons for resorting</label>
              <input
                type="number"
                className="input w-36 tabular-nums"
                min={1}
                max={row.producedQty}
                value={resortQty}
                onChange={(e) => setResortQty(e.target.value)}
              />
            </div>
            <p className="pb-2 text-xs text-slate-600">
              {!rsValid
                ? `Between 1 and ${row.producedQty.toLocaleString()}.`
                : rs === row.producedQty
                  ? 'The whole batch.'
                  : `${(row.producedQty - rs).toLocaleString()} approved now.`}
            </p>
          </div>
          <div>
            <label className="label">What to sort out</label>
            <textarea
              className="input min-h-[56px]"
              value={reason}
              maxLength={500}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. mixed with open pasting, dirty cartons in the lot"
            />
          </div>
          <p className="text-[11px] text-slate-600">
            These cartons cannot be dispatched until the contractor sorts them again and the Resorting entry is approved here.
          </p>
          <div className="flex gap-2">
            <button className="btn bg-amber-500 text-white hover:bg-amber-600" disabled={!rsValid || busy} onClick={() => resort.mutate()}>
              {resort.isPending ? 'Saving…' : 'Send for resorting'}
            </button>
            <button className="btn-ghost" disabled={busy} onClick={() => setResorting(false)}>Cancel</button>
          </div>
        </div>
      )}

      {canInspect && rejecting && (
        <div className="mt-3 space-y-2 rounded border border-red-200 bg-red-50 p-2.5">
          <div>
            <label className="label">Why rejected? *</label>
            <textarea
              className="input min-h-[64px]"
              value={reason}
              maxLength={500}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. colour shade off, pasting open, dirty print"
            />
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="label">Cartons rejected</label>
              <input
                type="number"
                className="input w-36 tabular-nums"
                min={1}
                max={row.producedQty}
                value={rejectedQty}
                onChange={(e) => setRejectedQty(e.target.value)}
              />
            </div>
            <p className="pb-2 text-xs text-slate-600">
              {!rejValid
                ? `Between 1 and ${row.producedQty.toLocaleString()}.`
                : rej === row.producedQty
                  ? 'The whole batch.'
                  : `${(row.producedQty - rej).toLocaleString()} accepted.`}
            </p>
          </div>
          <div>
            <label className="label">Photos / PDF</label>
            <input
              ref={fileInput}
              type="file"
              multiple
              accept="image/*,application/pdf"
              className="block w-full text-sm text-slate-700 file:mr-3 file:rounded file:border-0 file:bg-white file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-slate-700 file:shadow-sm"
              onChange={(e) => void addFiles(e.target.files)}
              disabled={files.length >= MAX_FILES}
            />
            {preparing && <p className="mt-1 text-xs text-slate-500">Preparing photos…</p>}
            {files.length > 0 && (
              <ul className="mt-1.5 space-y-1">
                {files.map((f, i) => (
                  <li key={`${f.name}-${i}`} className="flex items-center justify-between gap-2 rounded bg-white px-2 py-1 text-xs">
                    <span className="truncate">{f.type === 'application/pdf' ? '📄' : '🖼'} {f.name}</span>
                    <span className="shrink-0 text-slate-400">
                      {f.size < 1024 * 1024 ? `${Math.max(1, Math.round(f.size / 1024))} KB` : `${(f.size / 1024 / 1024).toFixed(1)} MB`}
                    </span>
                    <button className="shrink-0 text-red-600" onClick={() => setFiles((x) => x.filter((_, j) => j !== i))} aria-label="Remove">✕</button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="flex gap-2">
            <button className="btn bg-red-600 text-white hover:bg-red-700" disabled={!canReject || busy} onClick={() => reject.mutate()}>
              {reject.isPending ? 'Saving…' : 'Reject'}
            </button>
            <button className="btn-ghost" disabled={busy} onClick={() => { setRejecting(false); setFiles([]); }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function HistoryCard({ row, canReopen, reopening, onReopen }: {
  row: HistoryRow;
  canReopen: boolean;
  reopening: boolean;
  onReopen: () => void;
}) {
  const rejected = row.status === 'QC_REJECTED';
  const partly = row.status === 'QC_APPROVED' && row.rejectedQty > 0;
  const badge = rejected
    ? { cls: 'bg-red-100 text-red-700', text: 'Rejected' }
    : row.status === 'QC_REWORK'
      ? { cls: 'bg-amber-100 text-amber-800', text: `Resorting · ${row.reworkQty.toLocaleString()}` }
      : partly
        ? { cls: 'bg-amber-100 text-amber-800', text: `Approved · ${row.rejectedQty.toLocaleString()} rejected` }
        : { cls: 'bg-emerald-100 text-emerald-700', text: 'Approved' };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-800">
            Job <span className="font-mono">{jobNo(row.jobId)}</span>
            <span className="ml-2 text-xs font-normal text-slate-700">{row.jobName ?? ''}</span>
          </div>
          <div className="text-xs text-slate-500">
            {[row.clientName, row.operationName, row.contractor].filter(Boolean).join(' · ')}
            {' · '}{row.producedQty.toLocaleString()} cartons made {dayText(row.productionDate)}
          </div>
          <div className="text-xs text-slate-500">
            Checked {dayText(row.inspectionDate)}{row.inspectorName ? ` by ${row.inspectorName}` : ''}
          </div>
        </div>
        <span className={`shrink-0 rounded px-2 py-0.5 text-[11px] font-semibold ${badge.cls}`}>{badge.text}</span>
      </div>

      {row.rejectionReason && (
        <p className="mt-2 rounded bg-slate-50 px-2 py-1.5 text-sm text-slate-700">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Reason </span>{row.rejectionReason}
        </p>
      )}

      {(row.attachments.length > 0 || canReopen) && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {row.attachments.map((a) => (
            <button
              key={a.id}
              className="inline-flex max-w-[220px] items-center gap-1 rounded border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-brand hover:bg-slate-100"
              onClick={() => void openAttachment(a)}
              title={a.fileName}
            >
              <span>{a.contentType === 'application/pdf' ? '📄' : '🖼'}</span>
              <span className="truncate">{a.fileName}</span>
            </button>
          ))}
          {canReopen && (
            <button className="ml-auto text-xs text-slate-500 hover:text-slate-800 hover:underline" disabled={reopening} onClick={onReopen}>
              Reopen
            </button>
          )}
        </div>
      )}
    </div>
  );
}
